import type { ProductModifierGroupSchema } from "@repo/validators";
import type { z } from "zod";
import { useCallback } from "react";
import { Permissions } from "@repo/database";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import type { ProductModifierGroup } from "~/providers/product-provider";
import { api } from "~/lib/api";
import { useModifierGroupStore } from "~/stores/use-modifier-group-store";
import { usePermissionManager } from "./use-permission-manager";

export const useProductModifierGroupManager = () => {
  const { t } = useTranslation("common");

  const { handleProductModifierGroupUpdate } = useProductModifierGroupUpdates();

  const { hasPermission } = usePermissionManager();

  const { setCurrentModifierGroup, setModifierGroupFormOpen } =
    useModifierGroupStore();

  const { mutate: create } = api.productModifierGroup.create.useMutation({
    async onSuccess(result) {
      toast.success(
        t("create_item_success", {
          item: t("ProductModifierGroup.product_modifier_group"),
        }),
      );
      await handleProductModifierGroupUpdate(result);
    },
    onError() {
      toast.error(
        t("create_item_error", {
          item: t("ProductModifierGroup.product_modifier_group"),
        }),
      );
    },
  });

  const { mutate: update } = api.productModifierGroup.update.useMutation({
    async onSuccess(_, variables) {
      toast.success(
        t("update_item_success", {
          item: t("ProductModifierGroup.product_modifier_group"),
        }),
      );
      await handleProductModifierGroupUpdate(variables.id);
    },
    onError() {
      toast.error(
        t("update_item_error", {
          item: t("ProductModifierGroup.product_modifier_group"),
        }),
      );
    },
  });

  const createModifierHandler = async (
    modifierGroup: z.infer<typeof ProductModifierGroupSchema>,
  ) => {
    const { permission } = await hasPermission({
      requiredPermission: Permissions.update_product,
      label: t("Permissions.update_product_label"),
    });

    if (!permission) return false;

    return await new Promise<boolean>((resolve) => {
      create(modifierGroup, {
        onSuccess: () => {
          resolve(true);
        },
        onError: () => {
          resolve(false);
        },
      });
    });
  };

  const updateModifierHandler = async (
    id: string,
    modifierGroup: z.infer<typeof ProductModifierGroupSchema>,
  ) => {
    const { permission } = await hasPermission({
      requiredPermission: Permissions.update_product,
      label: t("Permissions.update_product_label"),
    });

    if (!permission) return false;

    return await new Promise<boolean>((resolve) => {
      update(
        {
          ...modifierGroup,
          id,
        },
        {
          onSuccess: () => {
            resolve(true);
          },
          onError: () => {
            resolve(false);
          },
        },
      );
    });
  };

  // const deleteProductHandler = async (id: string) => {
  //   const { permission } = await hasPermission({
  //     requiredPermission: Permissions.delete_product,
  //     label: t("Permissions.delete_product_label"),
  //   });

  //   if (!permission) return false;

  //   const result = await showDialog({
  //     title: t("before_delete_product_title"),
  //     message: t("before_delete_product_message"),
  //   });

  //   if (result === false) return false;

  //   return new Promise<boolean>((resolve) => {
  //     deleteProduct(id, {
  //       onSuccess() {
  //         resolve(true);
  //       },
  //       onError() {
  //         resolve(false);
  //       },
  //     });
  //   });
  // };

  const openDialog = (modifierGroup?: ProductModifierGroup) => {
    setModifierGroupFormOpen(true);
    setCurrentModifierGroup(modifierGroup);
  };
  return {
    openDialog,
    createModifierGroup: createModifierHandler,
    updateModifierGroup: updateModifierHandler,
  };
};

export const useProductModifierGroupUpdates = () => {
  const utils = api.useUtils();

  const handleProductModifierGroupUpdate = useCallback(
    async (groupId: string) => {
      const result = await utils.productModifierGroup.getById.fetch(groupId);

      void utils.productModifierGroup.getAll.setData(undefined, (old) => {
        if (!old) return old;
        return [...old.filter((p) => p.id !== result.id), result];
      });
    },
    [utils.productModifierGroup.getAll, utils.productModifierGroup.getById],
  );

  const handleProductModifierGroupDelete = useCallback(
    (groupId: string) => {
      void utils.productModifierGroup.getAll.setData(undefined, (old) => {
        if (!old) return old;
        return old.filter((p) => p.id !== groupId);
      });
    },
    [utils.productModifierGroup.getAll],
  );

  return {
    handleProductModifierGroupUpdate,
    handleProductModifierGroupDelete,
  };
};
